import { AlertTriangle, OctagonAlert } from 'lucide-react';
import { MarginBar } from '@/components/margin-bar';
import { ProfitBadge } from '@/components/profit-badge';
import { formatarFracaoPercentual, formatarMarkup, formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { CenarioLido } from './tipos';

/** "+8,00%" / "-6,06%" a partir da fracao com sinal da API. So reescreve o sinal. */
export function variacaoTexto(fracao: string | null | undefined): string {
  const t = formatarFracaoPercentual(fracao);
  if (t === '—') return t;
  return t.startsWith('-') ? t : `+${t}`;
}

/** Cor da variacao de preco: sobe = laranja, desce = verde (design). Decide pelo sinal da string. */
function tomVariacao(fracao: string | null): string {
  if (fracao === null || !/[1-9]/.test(fracao)) return 'text-ink-muted';
  return fracao.startsWith('-') ? 'text-success-ink' : 'text-warning-ink';
}

function moedaComSinal(v: string): string {
  const t = formatarMoeda(v);
  return t === '—' || t.startsWith('-') ? t : `+${t}`;
}

export interface CenarioNaTela extends CenarioLido {
  /** Identificador estavel (id do formulario; 0 = base). */
  chave: number;
}

function LucroNoPraticado({ c }: { c: CenarioLido }) {
  const p = c.resultado?.practiced;
  if (!c.resultado || !p) return <span className="text-ink-muted">—</span>;
  return (
    <div className="flex flex-col items-end gap-0.5">
      <ProfitBadge
        compacto
        lucro={p.simulatedProfit}
        lucroExibido={c.composicao?.praticado?.segmentos.lucro.fracao}
        meta={c.resultado.desiredProfitRate}
        minimo={c.composicao?.lucroMinimo ?? '0'}
      />
      {c.diferencas?.lucroSimulado != null && (
        <span className="tabular text-caption text-ink-muted">{variacaoTexto(c.diferencas.lucroSimulado).replace('%', ' p.p.')}</span>
      )}
    </div>
  );
}

export interface ComparacaoProps {
  base: CenarioNaTela;
  cenarios: CenarioNaTela[];
  selecionado: number;
  onSelecionar: (chave: number) => void;
  carregando?: boolean;
}

/** Tabela "Comparacao de cenarios" (design Simulador). Todo numero e o devolvido por POST /precificacao/cenarios. */
export function Comparacao({ base, cenarios, selecionado, onSelecionar, carregando }: ComparacaoProps) {
  const linhas = [base, ...cenarios];
  return (
    <section aria-label="Comparação de cenários" aria-busy={carregando} className={cn('min-w-0 rounded-2xl border border-border bg-card', carregando && 'opacity-70')}>
      <div className="border-b border-border px-5 py-4">
        <h2 className="text-title font-semibold">Comparação de cenários</h2>
        <div className="text-small text-ink-muted">Cada linha muda premissas em relação à base; os números vêm do motor.</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse text-small">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-5 py-2.5 font-medium">Cenário</th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">Preço sugerido</th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">vs base</th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">Margem a prazo</th>
              <th scope="col" className="px-5 py-2.5 text-right font-medium">Lucro no praticado</th>
            </tr>
          </thead>
          <tbody className="tabular">
            {linhas.map((c) => {
              const ehBase = c.chave === 0;
              return (
                <tr
                  key={c.chave}
                  data-testid={ehBase ? 'linha-base' : 'linha-cenario'}
                  aria-selected={!ehBase && c.chave === selecionado}
                  className={cn('border-t border-line-soft', ehBase && 'bg-surface-muted', !ehBase && c.chave === selecionado && 'bg-primary/5')}
                >
                  <th scope="row" className="px-5 py-3 text-left font-sans">
                    {ehBase ? (
                      <span className="font-semibold">{c.nome}</span>
                    ) : (
                      <button type="button" onClick={() => onSelecionar(c.chave)} className={cn('rounded px-1 text-left hover:underline', c.chave === selecionado ? 'font-semibold' : 'font-normal')}>
                        {c.nome}
                      </button>
                    )}
                  </th>
                  {c.erro || !c.composicao ? (
                    <td colSpan={4} className="px-3 py-3 font-sans text-danger-ink" role="alert">
                      <OctagonAlert aria-hidden="true" className="mr-1.5 inline size-4 align-text-bottom" />
                      {c.erro?.message ?? 'Sem resultado.'} <span className="text-caption text-ink-muted">código {c.erro?.code ?? '—'}</span>
                    </td>
                  ) : (
                    <>
                      <td className="px-3 py-3 text-right font-semibold" data-campo="sugerido">{formatarMoeda(c.composicao.sugerido.preco)}</td>
                      <td className="px-3 py-3 text-right" data-campo="vs-base">
                        {c.diferencas ? (
                          <div className={cn('flex flex-col items-end', tomVariacao(c.diferencas.variacaoSugerido))}>
                            <span>{variacaoTexto(c.diferencas.variacaoSugerido)}</span>
                            <span className="text-caption">{moedaComSinal(c.diferencas.sugerido)}</span>
                          </div>
                        ) : (
                          <span className="text-ink-muted">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right" data-campo="margem">
                        <div className="flex flex-col items-end">
                          <span>{formatarFracaoPercentual(c.composicao.sugerido.margemEfetivaAPrazo)}</span>
                          {c.diferencas && (
                            <span className="text-caption text-ink-muted">{variacaoTexto(c.diferencas.margemEfetivaAPrazo).replace('%', ' p.p.')}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right" data-campo="lucro">
                        <LucroNoPraticado c={c} />
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** "Para onde vai cada real" por cenario (MarginBar com as fracoes da API) e avisos do motor. */
export function CartoesCenarios({ itens }: { itens: CenarioNaTela[] }) {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4" data-testid="cartoes-cenarios">
      {itens.map((c) => (
        <section key={c.chave} aria-label={`Composição · ${c.nome}`} className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold">{c.nome}</h3>
            {c.composicao && <span className="tabular text-title font-semibold">{formatarMoeda(c.composicao.sugerido.preco)}</span>}
          </div>
          {c.erro || !c.composicao ? (
            <p className="text-small text-danger-ink">{c.erro?.message ?? 'Sem resultado.'}</p>
          ) : (
            <>
              <MarginBar
                titulo="Para onde vai cada real do sugerido (sem IPI)"
                segmentos={[
                  { chave: 'custo', rotulo: 'Custo', fracao: c.composicao.sugerido.segmentos.custo.fracao, valor: c.composicao.sugerido.segmentos.custo.valor },
                  { chave: 'variavel', rotulo: 'Desp. variável', fracao: c.composicao.sugerido.segmentos.despesaVariavel.fracao, valor: c.composicao.sugerido.segmentos.despesaVariavel.valor },
                  { chave: 'fixa', rotulo: 'Desp. fixa', fracao: c.composicao.sugerido.segmentos.despesaFixa.fracao, valor: c.composicao.sugerido.segmentos.despesaFixa.valor },
                  { chave: 'lucro', rotulo: 'Lucro', fracao: c.composicao.sugerido.segmentos.lucro.fracao, valor: c.composicao.sugerido.segmentos.lucro.valor },
                ]}
              />
              <div className="text-caption text-ink-muted">
                Markup {formatarMarkup(c.composicao.sugerido.markup ?? '')} · taxa financeira {formatarFracaoPercentual(c.composicao.sugerido.taxaFinanceira)}
              </div>
              {c.avisos.map((a) => (
                <div key={a.code} role="alert" className="flex items-start gap-2 rounded-lg border border-warning-dot/40 bg-warning-bg px-3 py-2 text-small text-ink-secondary">
                  <AlertTriangle aria-hidden="true" className="mt-px size-4 shrink-0 text-warning-ink" />
                  <span>
                    {a.message} <span className="text-caption text-ink-muted">({a.code})</span>
                  </span>
                </div>
              ))}
            </>
          )}
        </section>
      ))}
    </div>
  );
}
